/**
 * Início do supervisor.
 *
 * O supervisor não tinha página inicial: entrava e caía directamente nas
 * Disputas, sem ideia do que tinha pela frente. Este ecrã mostra as quatro
 * filas do âmbito dele e leva a cada uma.
 *
 * O Dashboard do admin não serve aqui — traz receita, utilizadores e
 * indicadores de negócio, que o supervisor nem sequer pode consultar (o
 * backend devolve 403).
 */
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, ShieldCheck, ClipboardList, ShieldAlert, Loader2, ArrowRight } from 'lucide-react';
import { getDisputes, getVerifications, getAdminTasks } from '../utils/api';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';

const Fila = ({ icone: Icone, cor, titulo, descricao, valor, aCarregar, onAbrir }) => (
  <button
    type="button"
    onClick={onAbrir}
    className="text-left p-4 rounded-xl border border-slate-800 bg-slate-800/40 hover:bg-slate-800 transition-colors"
  >
    <div className="flex items-start gap-3">
      <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ${cor}`}>
        <Icone className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-white tabular-nums">
            {aCarregar ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : valor}
          </span>
          <span className="text-sm font-medium text-slate-300">{titulo}</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">{descricao}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-600 shrink-0 mt-1" />
    </div>
  </button>
);

const SupervisorHome = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [n, setN] = useState({ disputas: 0, verificacoes: 0, tarefas: 0, integridade: 0 });
  const [aCarregar, setACarregar] = useState(true);

  useEffect(() => {
    let vivo = true;
    // Cada fila falha por si: uma indisponível não deve esconder as outras.
    Promise.allSettled([
      getDisputes({ status: 'open' }),
      getVerifications('pending'),
      getAdminTasks(),
      api.get('/admin/bypass', { params: { suspensos: 1 } }),
    ]).then(([d, v, t, b]) => {
      if (!vivo) return;
      const conta = (r) => {
        if (r.status !== 'fulfilled') return 0;
        const x = r.value?.data ?? r.value;
        if (Array.isArray(x)) return x.length;
        if (Array.isArray(x?.utilizadores)) return x.utilizadores.length;
        if (Array.isArray(x?.disputes)) return x.disputes.length;
        return 0;
      };
      setN({ disputas: conta(d), verificacoes: conta(v), tarefas: conta(t), integridade: conta(b) });
      setACarregar(false);
    });
    return () => { vivo = false; };
  }, []);

  const primeiro = (user?.full_name || '').trim().split(/\s+/)[0] || 'Supervisor';
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 19 ? 'Boa tarde' : 'Boa noite';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">{saudacao}, {primeiro}.</h1>
        <p className="text-sm text-slate-400 mt-1">O que está à sua espera hoje.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Fila
          icone={Scale} cor="bg-amber-500/15 text-amber-400"
          titulo="mediações abertas" descricao="Casos à espera de decisão"
          valor={n.disputas} aCarregar={aCarregar}
          onAbrir={() => navigate('/admin/disputes')}
        />
        <Fila
          icone={ShieldCheck} cor="bg-blue-500/15 text-blue-400"
          titulo="verificações pendentes" descricao="Documentos por validar"
          valor={n.verificacoes} aCarregar={aCarregar}
          onAbrir={() => navigate('/admin/verifications')}
        />
        <Fila
          icone={ClipboardList} cor="bg-purple-500/15 text-purple-400"
          titulo="tarefas atribuídas" descricao="Pedidos que lhe foram encaminhados"
          valor={n.tarefas} aCarregar={aCarregar}
          onAbrir={() => navigate('/admin/requests')}
        />
        <Fila
          icone={ShieldAlert} cor="bg-red-500/15 text-red-400"
          titulo="contas suspensas" descricao="Tentativas de sair da plataforma"
          valor={n.integridade} aCarregar={aCarregar}
          onAbrir={() => navigate('/admin/integrity')}
        />
      </div>

      <p className="text-xs text-slate-600">
        Reactivar contas suspensas e alterar definições da plataforma continuam reservados ao administrador.
      </p>
    </div>
  );
};

export default SupervisorHome;
